import { Injectable, inject } from '@angular/core';
import { environment } from '@env/environment';
import { isAtLeastLogLevel, type ClientLogLevel } from './client-log-level';
import { RemoteClientLogService } from './remote-client-log.service';
import {
  ClientLogContext,
  ClientLogEvent,
  ClientLogSource,
} from './client-log-event.model';
import {
  getTopStackFrame,
  serializeClientError,
} from './error-serializer';
import {
  sanitizeMetadata,
  sanitizeString,
  sanitizeUrl,
} from './safe-log-sanitizer';

const SESSION_STORAGE_KEY = 'electa.clientLogs.session';
const DEDUPE_WINDOW_MS = 30_000;

@Injectable({ providedIn: 'root' })
export class ClientLoggerService {
  private readonly remote = inject(RemoteClientLogService);
  private readonly sessionId = this.getOrCreateSessionId();
  private readonly recentFingerprints = new Map<string, number>();
  private readonly recentRemoteTimestamps: number[] = [];
  private remoteEventsThisSession = 0;
  private initialized = false;

  initialize(): void {
    this.runSafely(() => {
      if (this.initialized || typeof window === 'undefined') return;
      this.initialized = true;

      if (environment.logging.captureGlobalErrors) {
        window.addEventListener('error', (event) => {
          this.fatal('Unhandled browser error', event.error ?? event.message, {
            source: 'window-error',
            metadata: {
              filename: sanitizeUrl(event.filename),
              lineno: event.lineno,
              colno: event.colno,
            },
          });
        });
      }

      if (environment.logging.captureUnhandledRejections) {
        window.addEventListener('unhandledrejection', (event) => {
          this.fatal('Unhandled promise rejection', event.reason, {
            source: 'unhandled-rejection',
          });
        });
      }
    });
  }

  debug(message: string, metadata?: Record<string, unknown>): void {
    this.runSafely(() => this.writeToConsole('debug', message, metadata));
  }

  info(message: string, metadata?: Record<string, unknown>): void {
    this.runSafely(() => this.writeToConsole('info', message, metadata));
  }

  warn(message: string, metadata?: Record<string, unknown>, context?: ClientLogContext): void {
    this.runSafely(() => {
      this.writeToConsole('warn', message, metadata);
      this.reportRemote('warn', message, undefined, {
        ...context,
        metadata: this.mergeMetadata(metadata, context?.metadata),
      });
    });
  }

  error(message: string, error?: unknown, context?: ClientLogContext): void {
    this.runSafely(() => {
      this.writeToConsole('error', message, error ?? context?.metadata);
      this.reportRemote('error', message, error, context);
    });
  }

  fatal(message: string, error?: unknown, context?: ClientLogContext): void {
    this.runSafely(() => {
      this.writeToConsole('error', message, error ?? context?.metadata);
      this.reportRemote('fatal', message, error, context);
    });
  }

  private writeToConsole(
    level: Exclude<ClientLogLevel, 'fatal'>,
    message: string,
    detail?: unknown,
  ): void {
    if (!environment.logging.consoleEnabled && !environment.LOG) return;

    const method = level === 'debug' ? 'debug' : level;
    if (typeof console?.[method] !== 'function') return;

    if (detail === undefined) {
      console[method](message);
      return;
    }
    console[method](message, detail);
  }

  private reportRemote(
    level: 'warn' | 'error' | 'fatal',
    message: string,
    error: unknown,
    context: ClientLogContext = {},
  ): void {
    try {
      if (!environment.logging.remoteEnabled) return;
      if (!isAtLeastLogLevel(level, environment.logging.remoteMinLevel)) return;
      if (!this.canSendRemoteEvent()) return;

      const serialized = serializeClientError(error, message);
      const source: ClientLogSource = context.source ?? 'logger';
      const sanitizedMessage = sanitizeString(message || serialized.message, 1000);
      const stack = serialized.stack ? sanitizeString(serialized.stack, 10000) : undefined;
      const fingerprint = context.fingerprint ?? this.createFingerprint(
        level,
        source,
        sanitizedMessage,
        serialized.errorName,
        getTopStackFrame(stack),
      );

      if (this.isDuplicate(fingerprint)) return;

      const event: ClientLogEvent = {
        severity: level,
        source,
        message: sanitizedMessage,
        errorName: serialized.errorName,
        stack,
        fingerprint,
        pageUrl: sanitizeUrl(context.pageUrl ?? this.getCurrentUrl()),
        appVersion: environment.appVersion,
        environmentState: environment.state,
        sessionId: this.sessionId,
        componentName: context.componentName
          ? sanitizeString(context.componentName, 100)
          : undefined,
        metadata: sanitizeMetadata(context.metadata),
        occurredAt: new Date().toISOString(),
      };

      this.remoteEventsThisSession++;
      this.recentRemoteTimestamps.push(Date.now());
      this.remote.record(event);
    } catch {
      // Logging must never become a source of app errors.
    }
  }

  private canSendRemoteEvent(): boolean {
    if (environment.logging.sampleRate < 1 && Math.random() > environment.logging.sampleRate) {
      return false;
    }

    if (this.remoteEventsThisSession >= environment.logging.maxEventsPerSession) {
      return false;
    }

    const now = Date.now();
    const recent = this.recentRemoteTimestamps.filter((timestamp) => now - timestamp < 60_000);
    this.recentRemoteTimestamps.length = 0;
    this.recentRemoteTimestamps.push(...recent);

    return recent.length < environment.logging.maxEventsPerMinute;
  }

  private isDuplicate(fingerprint: string): boolean {
    const now = Date.now();
    const previous = this.recentFingerprints.get(fingerprint);
    this.recentFingerprints.set(fingerprint, now);
    return previous !== undefined && now - previous < DEDUPE_WINDOW_MS;
  }

  private createFingerprint(...parts: (string | undefined)[]): string {
    const input = parts.filter(Boolean).join('|').toLowerCase();
    let hash = 0;

    for (let index = 0; index < input.length; index++) {
      hash = (hash * 31 + input.charCodeAt(index)) >>> 0;
    }

    return hash.toString(36);
  }

  private getCurrentUrl(): string | undefined {
    return typeof window === 'undefined' ? undefined : window.location.href;
  }

  private getOrCreateSessionId(): string {
    if (typeof window === 'undefined') return this.generateUuid();

    try {
      const existing = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (existing) return existing;

      const next = this.generateUuid();
      sessionStorage.setItem(SESSION_STORAGE_KEY, next);
      return next;
    } catch {
      return this.generateUuid();
    }
  }

  private generateUuid(): string {
    try {
      if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
      }
    } catch {
      // fall through to Math.random-based fallback
    }

    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
      const random = Math.random() * 16 | 0;
      const value = char === 'x' ? random : (random & 0x3) | 0x8;
      return value.toString(16);
    });
  }

  private mergeMetadata(
    first?: Record<string, unknown>,
    second?: Record<string, unknown>,
  ): Record<string, unknown> | undefined {
    try {
      return { ...(first ?? {}), ...(second ?? {}) };
    } catch {
      return undefined;
    }
  }

  private runSafely(action: () => void): void {
    try {
      action();
    } catch {
      // Logging must never become a source of app errors.
    }
  }
}
